import {
  AlertOctagon,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Pencil,
  RefreshCw,
  ShieldCheck,
  VectorSquare,
  X,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getTaskTypeLabel, TaskType } from '../../../../../../db/api/task-types/task-main.js';
import { shouldShowOcrText } from '../../../../../../shared/bounding-box-content.js';
import { formatTaskReferenceForDisplay } from '../../../../../../shared/grammar-rules-resolution.js';
import { GRADING_THRESHOLDS } from '../../../../../lib/constants.js';
import { cn } from '../../../../../lib/utils.js';
import { MarkdownWithMath } from '../../../../markdown/markdown-with-math.js';
import { LatexRichEditor } from '../../../../ui/latex-rich-editor.js';
import { BoxLoadingOverlay } from '../../../shared/box-loading-overlay.js';
import type { GradingStateStore } from '../use-grading-state.js';
import {
  type ConfidenceLevel,
  getFirstSubmissionIndexWithLowConfidenceForTask,
  getFirstSubmissionIndexWithMediumConfidenceForTask,
  getLanguageUseConfidenceLevelForTaskAcrossSubmissions,
  hasLowConfidenceForTaskAcrossSubmissions,
  hasMediumConfidenceForTaskAcrossSubmissions,
} from '../utils.js';
import { BoxModeToggle } from './box-mode-toggle.js';

function formatTaskLabel(taskNumber: string, translate: (key: string, options?: { defaultValue?: string }) => string) {
  const normalizedTaskNumber = taskNumber.trim();
  if (!normalizedTaskNumber) return taskNumber;

  const segments = normalizedTaskNumber.split('.');
  const lastSegment = segments[segments.length - 1];
  if (!lastSegment) return normalizedTaskNumber;

  const taskType = Object.values(TaskType).includes(lastSegment as TaskType) ? (lastSegment as TaskType) : undefined;
  if (!taskType || taskType === TaskType.DEFAULT || taskType === TaskType.TASK_FRAME) return normalizedTaskNumber;

  segments[segments.length - 1] = getTaskTypeLabel(taskType, translate);
  return segments.join('.');
}

// Badge showing which LanguageUseScheme group is currently active for the task, with chevrons
// flanking the label to click through the other groups the task belongs to (a task can belong to
// more than one) — shown next to the score in AiGradingPanel and next to maxPoints in
// GradingSchemePanel.
function LanguageUseGroupNavBadge({ store }: { store: GradingStateStore }) {
  const { t } = useTranslation();
  const { computed, actions } = store;
  const activeSource = computed.activeLanguageUseSource;
  if (!activeSource) return null;

  const sources = computed.languageUseBoxSources;
  const activeIndex = sources.findIndex(source => source.key === activeSource.key);
  const hasMultipleGroups = sources.length > 1;

  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 self-start">
      {hasMultipleGroups && (
        <button
          type="button"
          disabled={activeIndex <= 0}
          onClick={() => actions.setActiveLanguageUseSourceKey(sources[activeIndex - 1]!.key)}
          className="p-1 pl-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
        >
          <ChevronLeft size={12} />
        </button>
      )}
      <span className={cn('text-[11px] font-medium py-1', hasMultipleGroups ? 'px-1' : 'px-2')}>
        {activeSource.displayNumber != null
          ? t('Canvas.grading.groupLanguageUseRuleNumbered', {
              number: activeSource.displayNumber,
              tasks: activeSource.dependsOn.map(formatTaskReferenceForDisplay).join(', '),
            })
          : t('Canvas.grading.groupLanguageUseRule', {
              tasks: activeSource.dependsOn.map(formatTaskReferenceForDisplay).join(', '),
            })}
      </span>
      {hasMultipleGroups && (
        <button
          type="button"
          disabled={activeIndex === -1 || activeIndex >= sources.length - 1}
          onClick={() => actions.setActiveLanguageUseSourceKey(sources[activeIndex + 1]!.key)}
          className="p-1 pr-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
        >
          <ChevronRight size={12} />
        </button>
      )}
    </div>
  );
}

function getVisibleSubmissionIndexes(currentIndex: number, total: number, windowSize = 5): number[] {
  if (total <= windowSize) return Array.from({ length: total }, (_, index) => index);

  const halfWindow = Math.floor(windowSize / 2);
  const maxStart = total - windowSize;
  const startIndex = Math.min(Math.max(currentIndex - halfWindow, 0), maxStart);

  return Array.from({ length: windowSize }, (_, offset) => startIndex + offset);
}

// The header gives things up in this order as it runs out of room. Which stage is active is decided
// by measuring the real layout (see useLayoutEffect in TopBar) — never by width breakpoints — so it
// collapses exactly when the zones stop fitting side by side, whatever the widths happen to be.
const HEADER_COLLAPSE_STAGES = [
  'nothing collapsed',
  'uncertainty label wraps to 2 lines',
  'uncertainty label drops to icon + count',
  'task label drops its "Aufgabe" prefix',
  'submission dots: 5 -> 4',
  'submission dots: 4 -> 3',
] as const;
const LAST_HEADER_COLLAPSE_STAGE = HEADER_COLLAPSE_STAGES.length - 1;

export function TopBar({ store }: { store: GradingStateStore }) {
  const { t } = useTranslation();
  const { state, computed, actions, refs } = store;
  const mode = computed.mode ?? 'correction';
  // Confidence helpers only distinguish answers vs correction; grammar mode reuses correction logic.
  const confidenceMode: 'answers' | 'correction' = mode === 'answers' ? 'answers' : 'correction';
  const currentTaskNumber =
    (computed.selectableTaskEntries[computed.currentSelectableTaskIndex] ?? computed.selectableTaskEntries[0])
      ?.fullTaskNumber ?? '';
  // Per-task worst language-use confidence across all submissions, so the dropdown row coloring
  // reflects language-use uncertainty too, not only content — see findLanguageUseConfidenceIssues in
  // utils.ts. Empty in answers mode: nothing has been graded yet there, content or language-use.
  const languageUseConfidenceLevelByTask = useMemo(
    () =>
      confidenceMode === 'answers'
        ? new Map<string, ConfidenceLevel>()
        : getLanguageUseConfidenceLevelForTaskAcrossSubmissions(computed.submissions, computed.languageUseSchemeRules),
    [confidenceMode, computed.submissions, computed.languageUseSchemeRules],
  );

  // `pass` is bumped on every width change purely to guarantee a re-render, and therefore a
  // re-measure in the layout effect below. Resetting the stage alone is not enough: when it is
  // already 0 React bails out of the render, the layout effect never runs, and the row silently
  // stops collapsing (it only appears to work until the first time you widen and narrow again).
  const [fit, setFit] = useState({ stage: 0, pass: 0 });
  const collapseStage = fit.stage;
  const measuredHeaderWidthRef = useRef(0);

  // A change in available width goes back to the widest layout; the layout effect below then walks
  // back down. Width only — a height change must not reset, or a stage that alters the row's height
  // would retrigger this and loop.
  useEffect(() => {
    const header = refs.headerRef.current;
    if (!header) return;
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width === measuredHeaderWidthRef.current) return;
      measuredHeaderWidthRef.current = width;
      setFit(previous => ({ stage: 0, pass: previous.pass + 1 }));
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, [refs.headerRef]);

  // Deliberately no dependency array: this runs after every render, so it steps down one stage per
  // render until the row fits — converging before the browser paints, so there's no visible jump.
  // The test is pure geometry: every zone is content-sized and nothing shrinks, so as soon as the
  // zones stop fitting side by side the last one's right edge crosses the row's content edge — which
  // is exactly the moment their borders would touch. No width thresholds involved anywhere.
  useLayoutEffect(() => {
    const header = refs.headerRef.current;
    const lastZone = header?.lastElementChild;
    if (!header || !lastZone || collapseStage >= LAST_HEADER_COLLAPSE_STAGE) return;
    const style = getComputedStyle(header);
    const contentRightEdge =
      header.getBoundingClientRect().right -
      parseFloat(style.borderRightWidth) -
      parseFloat(style.paddingRight);
    // Half a pixel of tolerance for sub-pixel layout rounding, not a threshold.
    if (lastZone.getBoundingClientRect().right > contentRightEdge + 0.5) {
      setFit(previous => ({ stage: previous.stage + 1, pass: previous.pass }));
    }
  });

  const uncertaintyLabelWraps = collapseStage === 1;
  const uncertaintyLabelHidden = collapseStage >= 2;
  const showTaskNamePrefix = collapseStage < 3;
  const submissionDotCount = collapseStage >= 5 ? 3 : collapseStage >= 4 ? 4 : 5;

  return (
    <div
      ref={refs.headerRef}
      className="flex items-center bg-white dark:bg-card p-2 rounded-xl border-2 border-slate-100 dark:border-slate-700 shadow-sm shrink-0 gap-2"
    >
      {/* All three zones are content-sized (shrink-0) and the slack lives in the two `grow` spacers
          between them: equal spacers put the uncertainty button exactly midway between this pill's
          right edge and the pagination's left edge, without any zone growing wider than its own
          content (which is what left dead whitespace inside this box before). Nothing shrinks, so
          "doesn't fit any more" surfaces as real overflow for the layout effect above to measure. */}
      <div className={cn('relative shrink-0', state.isTaskDropdownOpen && 'z-50')} ref={refs.dropdownRef}>
        <div className="flex items-center gap-0.5 bg-white dark:bg-card border-2 border-slate-100 dark:border-slate-700 rounded-xl p-0.5 shadow-sm">
          <button
            className="h-8 w-8 inline-flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-30 transition-colors"
            disabled={computed.currentSelectableTaskIndex <= 0}
            onClick={actions.goToPrevTask}
            title={t('Canvas.grading.prevTask')}
          >
            <ChevronLeft size={18} className="text-slate-600 dark:text-slate-400" />
          </button>

          <button
            onClick={() => actions.setIsTaskDropdownOpen(!state.isTaskDropdownOpen)}
            className={cn(
              'flex items-center justify-center gap-1 px-2 py-1 rounded-lg transition-all duration-200 text-sm font-medium text-slate-700 dark:text-slate-300',
              state.isTaskDropdownOpen
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : 'hover:bg-slate-50 dark:hover:bg-slate-700',
            )}
          >
            <span className="whitespace-nowrap">
              {showTaskNamePrefix
                ? t('Canvas.grading.taskName', {
                    number: formatTaskLabel(currentTaskNumber, t) || actions.getTaskName(),
                  })
                : formatTaskLabel(currentTaskNumber, t) || actions.getTaskName()}
            </span>
            <ChevronDown
              size={18}
              className={cn('text-slate-400 transition-transform', state.isTaskDropdownOpen && 'rotate-180')}
            />
          </button>

          <button
            className="h-8 w-8 inline-flex items-center justify-center hover:bg-slate-100 rounded-lg disabled:opacity-30 transition-colors"
            disabled={
              computed.currentSelectableTaskIndex < 0 ||
              computed.currentSelectableTaskIndex >= computed.selectableTaskEntries.length - 1
            }
            onClick={actions.goToNextTask}
            title={t('Canvas.grading.nextTask')}
          >
            <ChevronRight size={18} className="text-slate-600 dark:text-slate-400" />
          </button>
        </div>

        {state.isTaskDropdownOpen && (
          <div className="absolute left-0 mt-2 w-full max-h-64 overflow-y-auto bg-white dark:bg-card border border-slate-100 dark:border-slate-700 rounded-xl shadow-xl z-50 py-2 custom-scrollbar ease-in animate-in fade-in slide-in-from-top-2 min-w-[200px]">
            {computed.selectableTaskEntries.map((entry, index) => {
              const languageUseLevel =
                languageUseConfidenceLevelByTask.get(`${entry.mainTaskIdx}-${entry.subTaskIdx ?? 'main'}`) ?? 'none';
              const hasLowConfidence =
                hasLowConfidenceForTaskAcrossSubmissions(
                  computed.submissions,
                  entry.mainTaskIdx,
                  entry.subTaskIdx,
                  confidenceMode,
                ) || languageUseLevel === 'low';
              const hasMediumConfidence =
                hasMediumConfidenceForTaskAcrossSubmissions(
                  computed.submissions,
                  entry.mainTaskIdx,
                  entry.subTaskIdx,
                  confidenceMode,
                ) || languageUseLevel === 'medium';
              const isSelected =
                state.currentMainTaskIdx === entry.mainTaskIdx && state.currentSubTaskIdx === entry.subTaskIdx;
              return (
                <button
                  key={`${entry.mainTaskIdx}-${entry.subTaskIdx ?? 'main'}-${index}`}
                  className={cn(
                    'w-full text-left px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-1.5',
                    isSelected
                      ? hasLowConfidence
                        ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 font-semibold'
                        : hasMediumConfidence
                          ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 font-semibold'
                          : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold'
                      : hasLowConfidence
                        ? 'text-red-600 dark:text-red-400 hover:bg-red-50/50 dark:hover:bg-red-900/20'
                        : hasMediumConfidence
                          ? 'text-amber-700 dark:text-amber-400 hover:bg-amber-50/50 dark:hover:bg-amber-900/20'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700',
                  )}
                  style={{ paddingLeft: `${16 + entry.depth * 16}px` }}
                  onClick={() => {
                    const lowConfIdx = getFirstSubmissionIndexWithLowConfidenceForTask(
                      computed.submissions,
                      entry.mainTaskIdx,
                      entry.subTaskIdx,
                      confidenceMode,
                    );
                    const mediumConfIdx = getFirstSubmissionIndexWithMediumConfidenceForTask(
                      computed.submissions,
                      entry.mainTaskIdx,
                      entry.subTaskIdx,
                      confidenceMode,
                    );
                    actions.selectTask(entry);
                    if (lowConfIdx >= 0) actions.setCurrentSubmissionIdx(lowConfIdx);
                    else if (mediumConfIdx >= 0) actions.setCurrentSubmissionIdx(mediumConfIdx);
                    actions.setIsTaskDropdownOpen(false);
                  }}
                >
                  <span>{t('Canvas.grading.taskName', { number: formatTaskLabel(entry.fullTaskNumber, t) })}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="grow" aria-hidden />

      <div className="flex items-center justify-center shrink-0">
        {computed.combinedUncertainties.length > 0 && (
          <button
            onClick={actions.handleNextUncertainty}
            className="flex items-center gap-2 h-9 px-3 text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg transition-colors border border-amber-200 dark:border-amber-800"
            title={t('Canvas.grading.uncertaintiesLeft', { count: computed.combinedUncertainties.length })}
          >
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {!uncertaintyLabelHidden && (
              // The wrapped stage is bounded in `ch` (a font-relative unit, half the label's own
              // length) rather than pixels, so it breaks into two balanced lines at any font size,
              // and the tighter type keeps both lines inside the button's fixed h-9.
              <span
                className={cn(
                  uncertaintyLabelWraps
                    ? 'max-w-[13ch] text-left text-[10px] leading-tight [text-wrap:balance]'
                    : 'whitespace-nowrap',
                )}
              >
                {t('Canvas.grading.nextUncertainty', { count: computed.combinedUncertainties.length })}
              </span>
            )}
            {uncertaintyLabelHidden && (
              <span className="whitespace-nowrap">({computed.combinedUncertainties.length})</span>
            )}
          </button>
        )}
        {
          // (
          //   <button
          //     onClick={actions.openSummary}
          //     className={cn(
          //       'flex items-center px-2 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors border border-slate-200 dark:border-slate-600',
          //       !state.isCompact ? 'gap-2 px-3' : 'gap-1',
          //     )}
          //   >
          //     <ArrowLeftToLine size={16} className="shrink-0" />
          //     {!state.isCompact && (
          //       <span className="text-center leading-tight">
          //         {t('Canvas.grading.backToSummary1')}
          //         <br />
          //         {t('Canvas.grading.backToSummary2')}
          //       </span>
          //     )}
          //   </button>
          // )
        }
      </div>

      <div className="grow" aria-hidden />

      <div className="flex items-center justify-end shrink-0">
        <div className="flex flex-col items-center shrink-0">
          <span className="text-[10px] leading-none font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap">
            {computed.currentSubmission?.studentName ||
              t('Canvas.grading.submissionX', { id: computed.currentSubmission?._id?.substring(0, 4) })}
          </span>

          {computed.role !== 'student' && (
            // Chevrons are taken out of flow (absolute) and centered against this wrapper instead of
            // being flex siblings of the dots — that way the wrapper's own height is set by the dots
            // alone (keeping it flush under the name above), while the chevrons still land exactly
            // level with the dots because they're centered on that same (dots-only) height.
            <div className="relative flex items-center px-5 py-1">
              <button
                className="absolute left-0 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md disabled:opacity-30"
                disabled={state.currentSubmissionIdx === 0}
                onClick={() => actions.setCurrentSubmissionIdx(p => p - 1)}
              >
                <ChevronLeft size={14} className="text-slate-600 dark:text-slate-400" />
              </button>

              <div className={cn('flex', !state.isCompact ? 'space-x-1' : 'space-x-0.5')}>
                {getVisibleSubmissionIndexes(
                  state.currentSubmissionIdx,
                  computed.submissions.length,
                  submissionDotCount,
                ).map(idx => {
                  const submission = computed.submissions[idx];
                  const isSelected = state.currentSubmissionIdx === idx;
                  const confidenceLevel = computed.getConfidenceLevelForSubmission(submission);
                  return (
                    <button
                      key={idx}
                      onClick={() => actions.setCurrentSubmissionIdx(idx)}
                      className={cn(
                        'rounded-full transition-all',
                        !state.isCompact ? 'w-2.5 h-2.5' : 'w-2 h-2',
                        isSelected && 'scale-150',
                        confidenceLevel === 'low'
                          ? 'bg-red-500 hover:bg-red-600'
                          : confidenceLevel === 'medium'
                            ? 'bg-amber-500 hover:bg-amber-600'
                            : isSelected
                              ? 'bg-blue-600'
                              : 'bg-slate-300 hover:bg-slate-400',
                      )}
                      title={t('Canvas.grading.submissionX', { id: idx + 1 })}
                    />
                  );
                })}
              </div>

              <button
                className="absolute right-0 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md disabled:opacity-30"
                disabled={state.currentSubmissionIdx === computed.submissions.length - 1}
                onClick={() => actions.setCurrentSubmissionIdx(p => p + 1)}
              >
                <ChevronRight size={14} className="text-slate-600 dark:text-slate-400" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function MiddleBar({ store }: { store: GradingStateStore }) {
  const { t } = useTranslation();
  const { state, computed, actions } = store;

  return (
    <div
      className={cn(
        'relative py-1 shrink-0 flex items-center justify-center',
        !state.isCompact ? 'min-h-[36px]' : 'min-h-[32px]',
      )}
    >
      <div className={cn('flex justify-center items-center', !state.isCompact ? 'space-x-2' : 'space-x-1')}>
        {!state.isCompact && (
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider mr-2">
            {t('Canvas.grading.pages')}
          </span>
        )}
        {computed.pages.map((_, idx) => {
          const hasBoxes = computed.hasBoxesOnPage(idx);
          const isActive = state.currentPage === idx;

          const pageBoxes = state.localTaskBoxes.filter(b => b.pageNumber === idx);
          const unapprovedBoxes = pageBoxes.filter(b => b.approved !== true && b.confidence != null);
          const tier = unapprovedBoxes.some(b => (b.confidence as number) < GRADING_THRESHOLDS.LOW_CONFIDENCE)
            ? 'low'
            : unapprovedBoxes.some(b => (b.confidence as number) < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE)
              ? 'medium'
              : 'high';

          const colorClasses = !hasBoxes
            ? { dot: 'bg-slate-200 hover:bg-slate-300', active: 'bg-slate-400 ring-2 ring-slate-400 ring-offset-1' }
            : tier === 'low'
              ? {
                  dot: 'bg-red-100 text-red-700 ring-2 ring-red-400 ring-offset-1',
                  active: 'bg-red-600 text-white ring-red-600',
                }
              : tier === 'medium'
                ? {
                    dot: 'bg-orange-100 text-orange-700 ring-2 ring-orange-400 ring-offset-1',
                    active: 'bg-orange-500 text-white ring-orange-500',
                  }
                : {
                    dot: 'bg-blue-100 text-blue-700 ring-2 ring-blue-400 ring-offset-1',
                    active: 'bg-blue-600 text-white ring-blue-600',
                  };

          return (
            <button
              key={idx}
              onClick={() => actions.setCurrentPage(idx)}
              className={cn(
                'flex items-center justify-center rounded-full transition-all duration-200 font-bold',
                !state.isCompact
                  ? hasBoxes
                    ? `w-6 h-6 text-[10px] ${colorClasses.dot}`
                    : 'w-4 h-4 text-[0px] bg-slate-200 text-transparent hover:bg-slate-300'
                  : hasBoxes
                    ? `w-4 h-4 text-[9px] ${colorClasses.dot}`
                    : 'w-2.5 h-2.5 text-[0px] bg-slate-200 text-transparent hover:bg-slate-300',
                isActive && !hasBoxes && colorClasses.active,
                isActive && hasBoxes && colorClasses.active,
              )}
              title={t('Canvas.grading.pageX', { page: idx + 1 })}
            >
              {hasBoxes ? idx + 1 : ''}
            </button>
          );
        })}
      </div>

      {/* Not for an online submission: this draws a region to EXTRACT from, and the page behind it is the
          blank printed sheet — it would run OCR over nothing and hand back a box with no text panel to
          fill in (see ExtractedTextPanel). */}
      {!computed.showExcerptOnlyForMainTask && !computed.currentSubmission?.answersSubmittedOnline && (
        <button
          type="button"
          className={cn(
            'absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center justify-center text-slate-600 dark:text-slate-400 bg-white dark:bg-card border border-slate-200 dark:border-slate-700 rounded-md hover:border-blue-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-40',
            !state.isCompact ? 'w-7 h-7' : 'w-6 h-6',
          )}
          disabled={!computed.currentSubmission?._id || !computed.currentMainTask?._id}
          onClick={async () => {
            const newBox = {
              id: `box-local-${Date.now()}`,
              x: 10,
              y: 10,
              width: 20,
              height: 15,
              pageNumber: state.currentPage,
              ocrExtractedText: '',
            };
            actions.setLocalTaskBoxes(prev => [...prev, newBox]);
            actions.setSelectedBoxId(newBox.id);
            actions.setEditedText('');
            actions.setIsEditingText(true);
            await actions.saveUpdate(null, { boxCoordinates: newBox, addBoundingBox: true });
          }}
          title={t('Canvas.grading.addBoundingBox')}
        >
          <VectorSquare size={12} className={!state.isCompact ? 'w-3.5 h-3.5' : 'w-3 h-3'} />
        </button>
      )}
    </div>
  );
}

export function ExtractedTextPanel({ store }: { store: GradingStateStore }) {
  const { t } = useTranslation();
  const { state, computed, actions } = store;
  if (!state.selectedBoxId || computed.showExcerptOnlyForMainTask) return null;
  // NOTHING WAS EXTRACTED from an online submission — the student typed their answer, so this panel's
  // whole subject is missing: there is no transcription to read against the page, no OCR confidence to
  // colour it by, and nothing for "Geprüft" to mean. The answer itself is shown by the grading panels.
  if (computed.currentSubmission?.answersSubmittedOnline) return null;

  // An image-classified box has no transcribable text — only the cropped image (shown on the
  // canvas) is meaningful, so we hide the text editor/preview for it.
  const showText = shouldShowOcrText(computed.selectedBox?.contentType);

  const isTaskApproved = computed.mode !== 'answers' && state.localIsApproved;
  const selectedBoxConfidence = Number(computed.selectedBox?.confidence);
  const isBoxLowConfidence =
    !isTaskApproved &&
    computed.selectedBox?.approved !== true &&
    Number.isFinite(selectedBoxConfidence) &&
    selectedBoxConfidence < GRADING_THRESHOLDS.LOW_CONFIDENCE;
  const isBoxMediumConfidence =
    !isTaskApproved &&
    computed.selectedBox?.approved !== true &&
    Number.isFinite(selectedBoxConfidence) &&
    selectedBoxConfidence >= GRADING_THRESHOLDS.LOW_CONFIDENCE &&
    selectedBoxConfidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE;

  return (
    <div
      className={cn(
        'flex-1 min-h-0',
        'rounded-xl border shadow-sm overflow-hidden flex flex-col animate-in slide-in-from-bottom-2 fade-in duration-200',
        isBoxLowConfidence
          ? 'bg-red-50/40 dark:bg-red-900/10 border-red-400'
          : isBoxMediumConfidence
            ? 'bg-amber-50/40 dark:bg-amber-900/10 border-amber-300'
            : 'bg-white dark:bg-card border-slate-200 dark:border-slate-700',
      )}
    >
      <div
        className={cn(
          'flex justify-between items-center border-b px-4 py-2',
          isBoxLowConfidence
            ? 'bg-red-100/70 dark:bg-red-900/20 border-red-200'
            : isBoxMediumConfidence
              ? 'bg-amber-100/70 dark:bg-amber-900/20 border-amber-200'
              : 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900',
        )}
      >
        <span
          className={cn(
            'text-xs font-bold uppercase tracking-wider',
            isBoxLowConfidence
              ? 'text-red-700 dark:text-red-400'
              : isBoxMediumConfidence
                ? 'text-amber-700 dark:text-amber-400'
                : 'text-blue-700 dark:text-blue-400',
          )}
        >
          {t('Canvas.grading.extractedText')}
        </span>
        <div className="flex items-center gap-3">
          {!state.isEditingText &&
            (() => {
              const confidence = Number(computed.selectedBox?.confidence);
              if (computed.selectedBoxIsApproved)
                return (
                  <div className="inline-flex items-center rounded-full overflow-hidden text-xs shadow-sm select-none">
                    <span className="px-2.5 py-1.5 bg-white text-emerald-600">
                      <ShieldCheck className="w-[15px] h-[15px]" />
                    </span>
                    <span className="px-3 py-1.5 bg-emerald-100 text-emerald-700 font-semibold">
                      {t('Canvas.grading.verified')}
                    </span>
                  </div>
                );
              if (!Number.isFinite(confidence) || confidence >= GRADING_THRESHOLDS.MEDIUM_CONFIDENCE) return null;
              const isCritical = confidence < GRADING_THRESHOLDS.LOW_CONFIDENCE;
              return (
                <button
                  onClick={async () => {
                    actions.setLocalTaskBoxes(prev =>
                      prev.map(box => (box.id === state.selectedBoxId ? { ...box, approved: true } : box)),
                    );
                    const idx = state.localTaskBoxes.findIndex(b => b.id === state.selectedBoxId);
                    await actions.saveUpdate(`box-${idx}`, { boxApproved: true });
                  }}
                  disabled={computed.isSelectedBoxSaving}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                    isCritical
                      ? 'bg-white dark:bg-secondary border border-rose-200 text-rose-700 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20'
                      : 'bg-white dark:bg-secondary border border-amber-200 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20',
                  )}
                  title={t('Canvas.grading.bboxConfidence', { conf: Math.round(confidence * 100) })}
                >
                  {isCritical ? (
                    <AlertOctagon className="w-[15px] h-[15px]" />
                  ) : (
                    <AlertTriangle className="w-[15px] h-[15px]" />
                  )}
                  <span>{t('Canvas.grading.review')}</span>
                </button>
              );
            })()}

          {showText &&
            (!state.isEditingText ? (
              <button
                onClick={() => actions.setIsEditingText(true)}
                disabled={computed.isSelectedBoxSaving}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white dark:bg-secondary border rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed',
                  isBoxLowConfidence
                    ? 'text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300'
                    : isBoxMediumConfidence
                      ? 'text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-300'
                      : 'text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300',
                )}
              >
                <Pencil size={14} />
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => actions.setIsEditingText(false)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white dark:bg-secondary border rounded-lg transition-colors shadow-sm',
                    isBoxLowConfidence
                      ? 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                      : isBoxMediumConfidence
                        ? 'text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                        : 'text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700',
                  )}
                >
                  <X size={14} />
                </button>
                <button
                  onClick={async () => {
                    actions.setIsEditingText(false);
                    actions.setLocalTaskBoxes(prev =>
                      prev.map(box =>
                        box.id === state.selectedBoxId
                          ? { ...box, ocrExtractedText: state.editedText, confidence: 1, approved: true }
                          : box,
                      ),
                    );
                    const idx = state.localTaskBoxes.findIndex(b => b.id === state.selectedBoxId);
                    const textChanged = state.editedText !== (computed.selectedBox?.ocrExtractedText ?? '');
                    await actions.saveUpdate(`box-${idx}`, {
                      ...(textChanged ? { extractedText: state.editedText } : {}),
                      boxApproved: true,
                    });
                  }}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white border border-transparent rounded-lg transition-colors shadow-sm',
                    isBoxLowConfidence
                      ? 'bg-red-700 hover:bg-red-600'
                      : isBoxMediumConfidence
                        ? 'bg-amber-600 hover:bg-amber-500'
                        : 'bg-blue-700 hover:bg-blue-600',
                  )}
                >
                  <Check size={14} />
                </button>
              </div>
            ))}
          <div className="w-px h-4 bg-blue-200/60 ml-1"></div>
          <button
            onClick={() => actions.setSelectedBoxId(null)}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>
      </div>
      {/* Overlay outside the scroller — see the AiGradingPanel body for why. */}
      <div className="relative flex-1 min-h-0">
        {computed.isSelectedBoxSaving && (
          <BoxLoadingOverlay
            label={t('Canvas.grading.rerunningOcr', { defaultValue: 'Re-running text recognition…' })}
          />
        )}
        <div className="h-full overflow-auto p-4 text-sm text-slate-700 dark:text-slate-300">
          {!showText ? (
            <span className="text-slate-400 italic">{t('Canvas.grading.imageContent')}</span>
          ) : state.isEditingText ? (
            <LatexRichEditor
              value={state.editedText}
              onChange={actions.setEditedText}
              className="h-full border-blue-200 focus-within:ring-2 focus-within:ring-blue-500"
              examId={state.examId}
            />
          ) : (
            <div className="prose prose-sm max-w-none">
              {state.editedText ? (
                <MarkdownWithMath>{state.editedText}</MarkdownWithMath>
              ) : (
                <span className="text-slate-400 italic">{t('Canvas.grading.noTextExtracted')}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AiGradingPanel({ store }: { store: GradingStateStore }) {
  const { t } = useTranslation();
  const { state, computed, actions } = store;
  const panelRef = useRef<HTMLDivElement>(null);
  const reviewButtonRef = useRef<HTMLButtonElement>(null);
  const reviewTooltipHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [reviewTooltipPos, setReviewTooltipPos] = useState<{
    top: number;
    left: number;
    width: number;
    arrowLeft: number;
  } | null>(null);
  if (computed.mode === 'answers') return null;
  if (computed.showExcerptOnlyForMainTask) return null;

  const isLanguageUseBox = state.boxMode === 'languageUse';
  const displayedScore = isLanguageUseBox ? computed.languageUseDisplayedScore : computed.displayedScore;
  const maxScore = isLanguageUseBox ? computed.currentLanguageUseMaxScore : computed.currentMaxScore;
  const explanation = isLanguageUseBox
    ? computed.currentLanguageUseExplanation
    : computed.currentGradingResult?.explanation;
  const confidenceNum = isLanguageUseBox ? computed.currentLanguageUseConfidenceNum : computed.confidenceNum;
  const reviewerFeedback = isLanguageUseBox
    ? computed.currentLanguageUseRuleResult?.reviewerFeedback
    : computed.currentGradingResult?.reviewerFeedback;
  const isApproved = isLanguageUseBox ? state.localLanguageUseIsApproved : state.localIsApproved;
  const hasResult = isLanguageUseBox
    ? Boolean(computed.activeLanguageUseSource)
    : Boolean(computed.currentGradingResult);
  // Box coloring reflects the WORSE of content and language-use confidence, regardless of which box
  // mode is currently selected — see aiGradingConfidenceLevel in use-grading-state.ts.
  const panelStatus = computed.aiGradingConfidenceLevel === 'none' ? undefined : computed.aiGradingConfidenceLevel;

  const showReviewTooltip = () => {
    if (!reviewerFeedback) return;
    if (reviewTooltipHideTimeoutRef.current) {
      clearTimeout(reviewTooltipHideTimeoutRef.current);
      reviewTooltipHideTimeoutRef.current = null;
    }
    const buttonRect = reviewButtonRef.current?.getBoundingClientRect();
    const panelRect = panelRef.current?.getBoundingClientRect();
    if (buttonRect && panelRect) {
      setReviewTooltipPos({
        top: buttonRect.top - 8,
        left: panelRect.left,
        width: panelRect.width,
        arrowLeft: buttonRect.left + buttonRect.width / 2 - panelRect.left,
      });
    }
  };
  const scheduleHideReviewTooltip = () => {
    reviewTooltipHideTimeoutRef.current = setTimeout(() => setReviewTooltipPos(null), 150);
  };

  const isRegrading = isLanguageUseBox ? computed.isRegradingLanguageUse : computed.isRegradingContent;
  // Queued work is still work — same overlay, same edit lockout, only the wording says it has not started yet.
  const isRegradingQueued = isLanguageUseBox
    ? computed.isRegradingLanguageUseQueued
    : computed.isRegradingContentQueued;

  return (
    <div
      ref={panelRef}
      className={cn(
        'flex-1 min-h-0',
        'relative rounded-xl border shadow-sm overflow-hidden flex flex-col',
        panelStatus === 'low'
          ? 'bg-red-50/40 dark:bg-red-900/10 border-red-400'
          : panelStatus === 'medium'
            ? 'bg-amber-50/40 dark:bg-amber-900/10 border-amber-300'
            : 'bg-white dark:bg-card border-slate-200 dark:border-slate-700',
      )}
    >
      <div
        className={cn(
          '@container flex items-center justify-between border-b px-4 py-2 shrink-0 gap-0.5',
          panelStatus === 'low'
            ? 'bg-red-100/70 dark:bg-red-900/20 border-red-200'
            : panelStatus === 'medium'
              ? 'bg-amber-100/70 dark:bg-amber-900/20 border-amber-200'
              : 'bg-green-50/50 dark:bg-green-900/10 border-green-100 dark:border-green-900',
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={cn(
              'min-w-0 font-bold uppercase tracking-wider overflow-hidden text-ellipsis whitespace-nowrap text-[clamp(9px,3.5cqw,12px)] leading-[clamp(10px,5cqw,16px)]',
              panelStatus === 'low'
                ? 'text-red-700 dark:text-red-400'
                : panelStatus === 'medium'
                  ? 'text-amber-700 dark:text-amber-400'
                  : 'text-green-700 dark:text-green-400',
            )}
          >
            {t('Canvas.grading.aiGrading')}
          </span>
          <BoxModeToggle
            boxMode={state.boxMode}
            onChangeBoxMode={actions.setBoxMode}
            sources={computed.languageUseBoxSources}
            contentDotLevel={computed.contentConfidenceDotLevel}
            languageUseDotLevel={computed.languageUseConfidenceDotLevel}
          />
        </div>
        {!state.isEditingGrading ? (
          <div className="flex items-center gap-2 shrink-0">
            {hasResult &&
              !isLanguageUseBox &&
              computed.currentGradingResult?.gradingSource === 'ai_consistency_adjusted' && (
                <span
                  title={t('Canvas.grading.consistencyAdjusted')}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-900/20 dark:text-sky-400 dark:border-sky-800 shrink-0"
                >
                  <RefreshCw className="w-[13px] h-[13px]" />
                </span>
              )}
            {hasResult &&
              (() => {
                if (isApproved)
                  return (
                    <div className="inline-flex items-center rounded-full overflow-hidden text-xs shadow-sm select-none shrink-0">
                      <span className="px-2.5 py-1.5 bg-white text-emerald-600">
                        <ShieldCheck className="w-[15px] h-[15px]" />
                      </span>
                      <span className="px-3 py-1.5 bg-emerald-100 text-emerald-700 font-semibold">
                        {t('Canvas.grading.verified')}
                      </span>
                    </div>
                  );
                if (confidenceNum === null || confidenceNum >= GRADING_THRESHOLDS.MEDIUM_CONFIDENCE) return null;
                const isCritical = confidenceNum < GRADING_THRESHOLDS.LOW_CONFIDENCE;
                return (
                  <>
                    <button
                      ref={reviewButtonRef}
                      onClick={async () => {
                        if (isLanguageUseBox) {
                          actions.setLocalLanguageUseIsApproved(true);
                          await actions.saveUpdate(null, {
                            languageUseIsApproved: true,
                            target: computed.currentSaveTarget,
                          });
                        } else {
                          actions.setLocalIsApproved(true);
                          await actions.saveUpdate(null, { isApproved: true });
                        }
                      }}
                      onMouseEnter={showReviewTooltip}
                      onMouseLeave={scheduleHideReviewTooltip}
                      disabled={isRegrading}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                        isCritical
                          ? 'bg-white border border-rose-200 text-rose-700 hover:bg-rose-50'
                          : 'bg-white border border-amber-200 text-amber-700 hover:bg-amber-50',
                      )}
                    >
                      {isCritical ? (
                        <AlertOctagon className="w-[15px] h-[15px]" />
                      ) : (
                        <AlertTriangle className="w-[15px] h-[15px]" />
                      )}
                      <span className="hidden @[21rem]:inline">{t('Canvas.grading.review')}</span>
                    </button>
                    {reviewerFeedback &&
                      reviewTooltipPos &&
                      createPortal(
                        <div
                          onMouseEnter={showReviewTooltip}
                          onMouseLeave={scheduleHideReviewTooltip}
                          className="fixed z-50 -translate-y-full max-h-64 overflow-y-auto whitespace-pre-wrap break-words bg-slate-800 text-white text-xs font-medium px-2.5 py-1.5 rounded shadow-lg"
                          style={{
                            top: reviewTooltipPos.top,
                            left: reviewTooltipPos.left,
                            width: reviewTooltipPos.width,
                          }}
                        >
                          {reviewerFeedback}
                          <div
                            className="absolute top-full -translate-x-1/2 border-4 border-transparent border-t-slate-800"
                            style={{ left: reviewTooltipPos.arrowLeft }}
                          ></div>
                        </div>,
                        document.body,
                      )}
                  </>
                );
              })()}
            <button
              onClick={() => actions.setIsEditingGrading(true)}
              disabled={isRegrading}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white dark:bg-secondary border rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-secondary',
                panelStatus === 'low'
                  ? 'text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300'
                  : panelStatus === 'medium'
                    ? 'text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-300'
                    : 'text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-300',
              )}
            >
              <Pencil size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => actions.setIsEditingGrading(false)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white dark:bg-secondary border rounded-lg transition-colors shadow-sm',
                panelStatus === 'low'
                  ? 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                  : panelStatus === 'medium'
                    ? 'text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                    : 'text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700',
              )}
            >
              <X size={14} />
            </button>
            <button
              onClick={async () => {
                if (isLanguageUseBox) {
                  actions.setLocalLanguageUseIsApproved(true);
                  await actions.saveUpdate(null, {
                    languageUseExplanation: state.editedGradingText,
                    languageUseScore: state.editedScore,
                    languageUseIsApproved: true,
                    target: computed.currentSaveTarget,
                  });
                } else {
                  actions.setLocalIsApproved(true);
                  await actions.saveUpdate(null, {
                    gradingText: state.editedGradingText,
                    score: state.editedScore,
                    isApproved: true,
                    target: { kind: 'content' },
                  });
                }
                actions.setIsEditingGrading(false);
              }}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white border border-transparent rounded-lg transition-colors shadow-sm',
                panelStatus === 'low'
                  ? 'bg-red-700 hover:bg-red-600'
                  : panelStatus === 'medium'
                    ? 'bg-amber-600 hover:bg-amber-500'
                    : 'bg-green-700 hover:bg-green-600',
              )}
            >
              <Check size={14} />
            </button>
          </div>
        )}
      </div>
      {/* The overlay is a sibling of the SCROLLER, not a child of it: an absolutely positioned child is laid
          out against the scroll container's padding box, which scrolls away with the content — so it covered
          only whatever happened to be visible when it appeared, and vanished entirely if the box was already
          scrolled down. `min-h-0` because the flex item no longer carries `overflow-auto` itself. */}
      <div className="relative flex-1 min-h-0">
        {isRegrading && (
          <BoxLoadingOverlay
            label={
              isRegradingQueued
                ? t('Canvas.grading.regradingQueued', { defaultValue: 'Queued…' })
                : t('Canvas.grading.regrading', { defaultValue: 'Grading…' })
            }
          />
        )}
        <div className="h-full overflow-auto p-4 text-sm text-slate-700 dark:text-slate-300">
          {state.isEditingGrading ? (
            <div className="flex flex-col h-full gap-3">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-600">{t('Canvas.grading.points')}</span>
                <input
                  type="number"
                  min={0}
                  max={maxScore}
                  value={state.editedScore}
                  onChange={e => {
                    let val = Number(e.target.value);
                    if (val > maxScore) val = maxScore;
                    if (val < 0) val = 0;
                    actions.setEditedScore(val);
                  }}
                  className="w-16 p-1 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                />
                <span className="text-slate-500">/ {maxScore}</span>
              </div>
              <LatexRichEditor
                value={state.editedGradingText}
                onChange={actions.setEditedGradingText}
                className="flex-1 w-full border-blue-200 focus-within:ring-2 focus-within:ring-blue-500"
                examId={state.examId}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={cn(
                    'font-semibold text-xs px-2 py-0.5 rounded-md self-start',
                    panelStatus === 'low'
                      ? 'bg-red-100 text-red-800'
                      : panelStatus === 'medium'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-green-100 text-green-800',
                  )}
                >
                  {displayedScore} / {maxScore} {t('Canvas.grading.pts')}
                </span>
                {isLanguageUseBox && computed.activeLanguageUseSource?.kind === 'group' && (
                  <LanguageUseGroupNavBadge store={store} />
                )}
              </div>
              <div className="prose prose-sm max-w-none">
                {explanation ? (
                  <MarkdownWithMath>{explanation}</MarkdownWithMath>
                ) : (
                  <span className="text-slate-400 italic">{t('Canvas.grading.noGradingYet')}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function GradingSchemePanel({ store }: { store: GradingStateStore }) {
  const { t } = useTranslation(); // <-- NEU
  const { state, computed, actions } = store;
  if (computed.mode === 'answers') return null;

  const isExpanded = !state.isGradingSchemeCollapsed;
  const sizingCls = isExpanded ? 'flex-1 min-h-0' : 'shrink-0';
  const isLanguageUseBox = state.boxMode === 'languageUse';
  const criteria = isLanguageUseBox ? computed.currentLanguageUseCriteria : computed.gradingSchemeCriteria;
  const maxPoints = isLanguageUseBox ? computed.currentLanguageUseMaxScore : computed.gradingSchemeMaxPoints;

  const isRefining = isLanguageUseBox
    ? computed.isGradingSchemeLanguageUseRefining
    : computed.isGradingSchemeContentRefining;

  return (
    <div
      className={cn(
        sizingCls,
        'relative rounded-xl border border-violet-100 dark:border-violet-900 shadow-sm overflow-hidden flex flex-col bg-white dark:bg-card',
      )}
    >
      <div className="flex justify-between items-center border-b border-violet-100 dark:border-violet-900 px-4 py-2 bg-violet-50/50 dark:bg-violet-900/10 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardList size={16} className="text-violet-700 shrink-0" />
          <span className="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-400 shrink-0">
            {t('Canvas.grading.gradingScheme')}
          </span>
        </div>
        <button
          onClick={() => actions.setIsGradingSchemeCollapsed(!state.isGradingSchemeCollapsed)}
          className="text-slate-400 hover:text-slate-600 transition-colors p-1 shrink-0"
          title={state.isGradingSchemeCollapsed ? t('Canvas.grading.expand') : t('Canvas.grading.collapse')}
        >
          <ChevronDown size={16} strokeWidth={2.5} className={cn('transition-transform', isExpanded && 'rotate-180')} />
        </button>
      </div>
      {/* Overlay outside the scroller — see the AiGradingPanel body for why. */}
      {isExpanded && (
        <div className="relative flex-1 min-h-0">
          {isRefining && (
            <BoxLoadingOverlay
              label={t('Canvas.msgs.gradingSchemeRefining', { defaultValue: 'Updating grading scheme…' })}
            />
          )}
          <div className="h-full p-4 overflow-y-auto text-sm text-slate-800 dark:text-slate-200 leading-relaxed custom-scrollbar">
            <div className="mb-2 flex items-center gap-2 flex-wrap">
              <div className="text-xs font-medium text-violet-700 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 inline-block px-2 py-1 rounded-md border border-violet-100 dark:border-violet-900">
                {t('Canvas.grading.maxPoints', { points: maxPoints })}
              </div>
              {isLanguageUseBox && computed.activeLanguageUseSource?.kind === 'group' && (
                <LanguageUseGroupNavBadge store={store} />
              )}
            </div>
            {criteria ? (
              <MarkdownWithMath>{criteria}</MarkdownWithMath>
            ) : (
              <span className="text-slate-400 italic">{t('Canvas.grading.noGradingSchemeYet')}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
